import { GridLegacy as Grid } from "@mui/material";
import { useTranslation } from "react-i18next";
import NotchedCard from "../../components/cards/NotchedCard/NotchedCard";
import EditableField, { EditableFieldTypes } from "../../components/editable/EditableField/EditableField";
import EditableGroup, { EditableGroupType } from "../../components/editable/EditableGroup/EditableGroup";
import type { IPreferences } from "../../logic/api";
import styles from "./styles";

type PaymentMethodPreferences = Pick<IPreferences,
  | "payment_type_cash_enabled"
  | "payment_type_credit_card_enabled"
  | "payment_type_debit_card_enabled"
  | "payment_type_credit_debit_card_enabled"
  | "payment_type_comp_enabled"
  | "payment_type_account_enabled"
  | "payment_type_refund_enabled"
>;

interface PaymentMethodSettingsProps {
  preferences: PaymentMethodPreferences;
  onSave: (preferences: Partial<IPreferences>) => Promise<boolean>;
}

export function PaymentMethodSettings({ preferences, onSave }: PaymentMethodSettingsProps) {
  const { t } = useTranslation();

  return (
    <Grid item xs={12}>
      <NotchedCard
        containerProps={{ 'data-testid': 'payment-method-settings' }}
        label={t("settings.paymentMethods")}
        style={styles.card}
      >
        <EditableGroup<PaymentMethodPreferences>
          id="paymentMethodSettings"
          testId="settings-payment-methods-group"
          editType={EditableGroupType.switchableEditing}
          data={preferences}
          onDataChanged={(data) => onSave({
            payment_type_cash_enabled: data.payment_type_cash_enabled,
            payment_type_credit_card_enabled: data.payment_type_credit_card_enabled,
            payment_type_debit_card_enabled: data.payment_type_debit_card_enabled,
            payment_type_credit_debit_card_enabled: data.payment_type_credit_debit_card_enabled,
            payment_type_comp_enabled: data.payment_type_comp_enabled,
            payment_type_account_enabled: data.payment_type_account_enabled,
            payment_type_refund_enabled: data.payment_type_refund_enabled,
          })}
        >
          <Grid container>
            <EditableField<PaymentMethodPreferences, boolean>
              inputType={EditableFieldTypes.switch}
              label={t("settings.cash")}
              onProvide={(data) => data.payment_type_cash_enabled}
              onChange={(data, value) => data.payment_type_cash_enabled = value}
              testId="payment-method-cash-toggle"
            />
            <EditableField<PaymentMethodPreferences, boolean>
              inputType={EditableFieldTypes.switch}
              label={t("settings.creditCard")}
              onProvide={(data) => data.payment_type_credit_card_enabled}
              onChange={(data, value) => data.payment_type_credit_card_enabled = value}
              testId="payment-method-credit-card-toggle"
            />
            <EditableField<PaymentMethodPreferences, boolean>
              inputType={EditableFieldTypes.switch}
              label={t("settings.debitCard")}
              onProvide={(data) => data.payment_type_debit_card_enabled}
              onChange={(data, value) => data.payment_type_debit_card_enabled = value}
              testId="payment-method-debit-card-toggle"
            />
            <EditableField<PaymentMethodPreferences, boolean>
              inputType={EditableFieldTypes.switch}
              label={t("settings.creditDebitCard")}
              onProvide={(data) => data.payment_type_credit_debit_card_enabled}
              onChange={(data, value) => data.payment_type_credit_debit_card_enabled = value}
              testId="payment-method-credit-debit-card-toggle"
            />
            <EditableField<PaymentMethodPreferences, boolean>
              inputType={EditableFieldTypes.switch}
              label={t("payments.complimentary")}
              onProvide={(data) => data.payment_type_comp_enabled}
              onChange={(data, value) => data.payment_type_comp_enabled = value}
              testId="payment-method-comp-toggle"
            />
            <EditableField<PaymentMethodPreferences, boolean>
              inputType={EditableFieldTypes.switch}
              label={t("settings.onAccount")}
              onProvide={(data) => data.payment_type_account_enabled}
              onChange={(data, value) => data.payment_type_account_enabled = value}
              testId="payment-method-account-toggle"
            />
            <EditableField<PaymentMethodPreferences, boolean>
              inputType={EditableFieldTypes.switch}
              label={t("settings.refund")}
              onProvide={(data) => data.payment_type_refund_enabled}
              onChange={(data, value) => data.payment_type_refund_enabled = value}
              testId="payment-method-refund-toggle"
            />
          </Grid>
        </EditableGroup>
      </NotchedCard>
    </Grid>
  );
}
